import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    Stack,
    InputAdornment,
    IconButton,
    CircularProgress
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import usePlatformAuth from '../hooks/usePlatformAuth';

const FEATURES = [
    'Multi-tenant management',
    'Company onboarding & setup',
    'User session monitoring',
    'Platform analytics & insights',
    'System configuration'
];

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = usePlatformAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const from = location.state?.from?.pathname || '/dashboard';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await login(email, password);
            if (result.success) {
                navigate(from, { replace: true });
            } else {
                setError(result.error || 'Login failed');
            }
        } catch (err) {
            setError('An error occurred during login');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                backgroundColor: 'var(--c-f8f9fa)',
            }}
        >
            {/* Left Side - Hero Panel (Twisted) */}
            <Box
                sx={{
                    flex: { lg: '0 0 45%' },
                    display: { xs: 'none', lg: 'flex' },
                    flexDirection: 'column',
                    justifyContent: 'center',
                    position: 'relative',
                    backgroundColor: 'var(--c-brand)',
                    overflow: 'hidden',
                }}
            >
                {/* Octagon Grid Pattern Background */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18 0h24L60 18v24L42 60H18L0 42V18L18 0z' fill='none' stroke='var(--rgba-255-255-255-a0_12)' stroke-width='1'/%3E%3C/svg%3E")`,
                        backgroundSize: '60px 60px',
                    }}
                />

                {/* Content */}
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        px: { lg: 6, xl: 8 },
                        py: 6,
                    }}
                >
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            color: 'var(--c-ffffff)',
                            mb: 2,
                            fontSize: { lg: '32px', xl: '36px' },
                            lineHeight: 1.2,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Platform Administration
                    </Typography>

                    <Typography
                        sx={{
                            color: 'var(--rgba-255-255-255-a0_9)',
                            mb: 5,
                            fontSize: '16px',
                            lineHeight: 1.6,
                            maxWidth: '420px',
                        }}
                    >
                        Manage tenants, subscriptions, and platform settings from a single dashboard.
                    </Typography>

                    {/* Features List */}
                    <Stack spacing={2.5}>
                        {FEATURES.map((feature, index) => (
                            <Stack
                                key={index}
                                direction="row"
                                spacing={2}
                                alignItems="center"
                            >
                                <CheckCircleIcon
                                    sx={{
                                        color: 'var(--c-ffffff)',
                                        fontSize: 22,
                                    }}
                                />
                                <Typography
                                    sx={{
                                        color: 'var(--c-ffffff)',
                                        fontSize: '15px',
                                        fontWeight: 500,
                                    }}
                                >
                                    {feature}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            </Box>

            {/* Right Side - Form Area (Twisted) */}
            <Box
                sx={{
                    flex: { xs: 1, lg: '0 0 55%' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    px: { xs: 3, sm: 6, md: 8, lg: 10 },
                    py: { xs: 4, sm: 6 },
                    backgroundColor: 'var(--c-ffffff)',
                }}
            >
                <Box sx={{ maxWidth: 440, width: '100%', mx: 'auto' }}>
                    {/* Logo */}
                    <Box sx={{ mb: 5 }}>
                        <img
                            src="/flonube_logo.svg"
                            alt="Flonube"
                            style={{ height: 40 }}
                        />
                    </Box>

                    {/* Title */}
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            color: 'var(--c-1a1a1a)',
                            mb: 1,
                            fontSize: { xs: '26px', sm: '32px' },
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Welcome back
                    </Typography>
                    <Typography
                        sx={{
                            color: 'var(--c-6b7280)',
                            mb: 4,
                            fontSize: '15px',
                        }}
                    >
                        Sign in to access the admin dashboard
                    </Typography>

                    {/* Login Form */}
                    <Box component="form" onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: 'var(--c-374151)',
                                        mb: 1,
                                    }}
                                >
                                    Email address
                                </Typography>
                                <TextField
                                    fullWidth
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="admin@flownube.com"
                                    required
                                    disabled={loading}
                                    autoComplete="email"
                                    autoFocus
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon sx={{ color: 'var(--c-9ca3af)', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            borderRadius: '12px',
                                            backgroundColor: 'var(--c-ffffff)',
                                            height: 52,
                                            '& fieldset': { borderColor: 'var(--c-e5e7eb)' },
                                            '&:hover fieldset': { borderColor: 'var(--c-brand) !important' },
                                            '&.Mui-focused fieldset': { borderColor: 'var(--c-brand) !important' },
                                        }
                                    }}
                                />
                            </Box>

                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: 'var(--c-374151)',
                                        mb: 1,
                                    }}
                                >
                                    Password
                                </Typography>
                                <TextField
                                    fullWidth
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    required
                                    disabled={loading}
                                    autoComplete="current-password"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockIcon sx={{ color: 'var(--c-9ca3af)', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                    size="small"
                                                    sx={{
                                                        color: 'var(--c-9ca3af)',
                                                        '&:hover': {
                                                            color: 'var(--c-brand)',
                                                            backgroundColor: 'var(--brand-a0_08)',
                                                        },
                                                    }}
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOffIcon sx={{ fontSize: 20 }} />
                                                    ) : (
                                                        <VisibilityIcon sx={{ fontSize: 20 }} />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            borderRadius: '12px',
                                            backgroundColor: 'var(--c-ffffff)',
                                            height: 52,
                                            '& fieldset': { borderColor: 'var(--c-e5e7eb)' },
                                            '&:hover fieldset': { borderColor: 'var(--c-brand) !important' },
                                            '&.Mui-focused fieldset': { borderColor: 'var(--c-brand) !important' },
                                        }
                                    }}
                                />
                            </Box>

                            {/* Error Alert */}
                            {error && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        borderRadius: '12px',
                                        border: '1px solid var(--c-fecaca)',
                                    }}
                                >
                                    {error}
                                </Alert>
                            )}

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={loading}
                                endIcon={!loading && <ArrowForwardIcon />}
                                sx={{
                                    height: 52,
                                    fontSize: '16px',
                                    borderRadius: '12px',
                                    backgroundColor: 'var(--c-brand)',
                                    boxShadow: '0 4px 14px 0 var(--brand-a0_3)',
                                    '&:hover': {
                                        backgroundColor: 'var(--c-brand-hover)',
                                        boxShadow: '0 6px 20px 0 var(--brand-a0_4)',
                                    },
                                }}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={22} color="inherit" sx={{ mr: 1 }} />
                                        Signing in...
                                    </>
                                ) : (
                                    'Sign in'
                                )}
                            </Button>
                        </Stack>
                    </Box>

                    {/* Footer Note */}
                    <Typography
                        sx={{
                            color: 'var(--c-9ca3af)',
                            fontSize: '13px',
                            textAlign: 'center',
                            mt: 4,
                        }}
                    >
                        Protected area. Authorized personnel only.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
